import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight, Crosshair } from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";

/**
 * Section dédiée à la localisation visiteur — style "Votre électricien intervient à [VILLE] et aux alentours".
 * Affichée sur la home, après le hero, pour confirmer immédiatement la couverture locale.
 */
export default function LocalCoverage() {
  const { displayCity, city, source, requestPreciseLocation } = useGeo();

  const heading = city
    ? `Votre électricien intervient à ${city} et aux alentours.`
    : `Votre électricien intervient ${displayCity}.`;

  const seoCopy = city
    ? `Vous recherchez un électricien à ${city} pour un dépannage rapide ou une installation électrique ? Nous intervenons pour les pannes électriques, tableaux électriques, prises, éclairages, mises aux normes et urgences électriques.`
    : `Vous recherchez un électricien pour un dépannage rapide ou une installation électrique ? Nous intervenons pour les pannes électriques, tableaux électriques, prises, éclairages, mises aux normes et urgences électriques.`;

  return (
    <section
      data-testid="local-coverage"
      className="bg-[#0F172A] text-white"
      aria-label="Zone de couverture locale"
    >
      <div className="container-x py-14 lg:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-[#EAB308] text-slate-900 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest mb-6">
            <MapPin className="w-4 h-4" strokeWidth={2.5} />
            <span data-testid="coverage-city-tag">{city ? city : "Votre secteur"}</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            {heading}
          </h2>
          <p
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl"
            data-testid="coverage-seo"
          >
            {seoCopy}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`tel:${SITE.phoneTel}`} data-testid="coverage-call-btn" className="btn-primary">
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              {SITE.phoneDisplay}
            </a>
            <Link to="/contact" className="bg-white text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-sm transition-colors">
              Demander un devis <ArrowRight className="w-4 h-4" />
            </Link>
            {source !== "browser-gps" && (
              <button
                type="button"
                onClick={requestPreciseLocation}
                data-testid="precise-location-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 border border-white/30 text-white hover:bg-white/10 transition-colors font-medium text-sm rounded-sm"
              >
                <Crosshair className="w-4 h-4" />
                Affiner ma localisation
              </button>
            )}
          </div>
        </div>

        <ul className="lg:col-span-5 grid gap-4">
          {[
            { icon: MapPin, title: "Intervention locale", desc: city ? `Secteur ${city} et alentours` : "Près de chez vous" },
            { icon: Clock, title: "Délai d'arrivée", desc: "30 minutes en moyenne en zone urbaine" },
            { icon: Phone, title: "Disponible 7j/7", desc: "Urgences traitées 24h/24" },
          ].map((it) => (
            <li key={it.title} className="flex items-start gap-4 border border-white/10 p-5">
              <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                <it.icon className="w-5 h-5" strokeWidth={2} />
              </span>
              <div>
                <p className="font-heading font-semibold text-base">{it.title}</p>
                <p className="text-sm text-slate-300 mt-1">{it.desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
